/**
 * Token Store Module
 * Stores the current Okta access token for use in axios interceptors.
 * Exposes a "when ready" promise so request interceptors can wait for the token
 * on first load (avoids 403 when page is refreshed and AuthTokenSync sets token in useEffect).
 */

let currentToken = null;

let tokenReadyResolve = null;
let tokenReadyPromise = new Promise((resolve) => {
  tokenReadyResolve = resolve;
});

/**
 * Set the current access token (called by AuthTokenSync when Okta auth is ready).
 * Resolves the "token ready" promise so any waiting request interceptors can proceed.
 */
export const setAccessToken = (token) => {
  currentToken = token;
  if (token && tokenReadyResolve) {
    tokenReadyResolve(token);
    tokenReadyResolve = null;
  }
};

/**
 * Get the current access token
 * @returns {string|null} The current access token or null if not set
 */
export const getAccessToken = () => {
  return currentToken;
};

/**
 * Returns a promise that resolves with the token once setAccessToken has been called with a truthy value.
 * Resolves immediately if the token is already set.
 * Used by axios interceptor on refresh so first requests wait for AuthTokenSync to run.
 * @returns {Promise<string|null>}
 */
export const getTokenWhenReady = () => {
  if (currentToken) return Promise.resolve(currentToken);
  return Promise.race([
    tokenReadyPromise,
    new Promise((resolve) => setTimeout(() => resolve(null), 4000)),
  ]);
};

/**
 * Clear the current access token and re-arm the ready promise.
 */
export const clearAccessToken = () => {
  currentToken = null;
  // Re-arm so the next setAccessToken call unblocks getTokenWhenReady().
  tokenReadyPromise = new Promise((resolve) => {
    tokenReadyResolve = resolve;
  });
};
